import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from './components/ui/theme-provider';
import { Toaster } from './components/ui/toaster';
import { HelmetProvider } from 'react-helmet-async'; // Ensure this import is present
import { getAuth, onAuthStateChanged } from 'firebase/auth'; // Ensure onAuthStateChanged is imported
import { doc, getDocFromServer, onSnapshot, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore'; // Ensure serverTimestamp is imported
import { db } from './firebase';
import { userService } from './services/userService';
import { resolveUserProfileDocument } from './services/profileBootstrap';
import { subscriptionService } from './services/subscriptionService';
import { decryptData } from './encryptionUtil';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { practiceDataUtil } from './utils/practiceDataUtil'; // LEGACY module (see file header) — NOT the same as helpers/practiceDataUtil used by Invoice/Daily flows
import { useToast } from './hooks/use-toast'; // This line may be missing or have the wrong path

// Import components
import Splashscreen from './Splashscreen';
import HygienistInvoice from './HygienistInvoice';
import BillingSettings from './components/BillingSettings';
import MainLayout from './components/layout/MainLayout';
import ProfileSettings from './ProfileSettings';
import ProtectedRoute from './components/ProtectedRoute'; // Make sure this is correctly imported
import OnboardingRedirect from './components/OnboardingRedirect';
import InvoiceHistory from './components/InvoiceHistory';
import Reports from './components/reports/Reports';
import TaxDashboard from './components/tax/TaxDashboard';
import PracticeMgmtAdmin from './components/admin/PracticeMgmtAdmin';
import { LoadingSpinner } from './components/ui/loading-spinner';
import ExpensesPage from './components/expenses/ExpensesPage';
// Fix the InvoiceApp import - make sure it's imported correctly
import InvoiceAppWithContext from './pages/InvoiceApp';
import InvoiceDetails from './components/InvoiceDetails';
import SupportPage from './pages/SupportPage'; // Add this import
import DataMergeTool from './pages/DataMergeTool'; // Add this import
import SocialMediaManager from './components/admin/social/SocialMediaManager'; // Social Media Management
import DashboardPage from './pages/DashboardPage'; // Import the new Dashboard page
import ResourcesPage from './pages/ResourcesPage'; // Import the new Resources page
import ArchiveManager from './components/admin/ArchiveManager'; // Import ArchiveManager
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'; 
import TermsOfServicePage from './pages/TermsOfServicePage'; 
import NotificationManager from './components/admin/NotificationManager'; // Import NotificationManager
import EmailAdminPage from './pages/admin/EmailAdminPage';
import EmailTemplateAdmin from './pages/admin/EmailTemplateAdmin';
import ReminderDashboard from './pages/admin/ReminderDashboard'; // Add this import
import AdminDashboard from './components/admin/AdminDashboard'; // Import AdminDashboard
import UserMapPage from './pages/admin/UserMapPage'; // ADDED: Import UserMapPage
import PracticeDashboardPage from './pages/practice/PracticeDashboardPage';
import PracticeJobPostingPage from './pages/practice/PracticeJobPostingPage';
import PracticeViewJobsPage from './pages/practice/PracticeViewJobsPage';
import ManagePracticesPage from './pages/practice/ManagePracticesPage'; // ADDED: Import ManagePracticesPage
import PracticeVerificationAdmin from './components/admin/PracticeVerificationAdmin'; // ADDED
import JobPostingForm from './components/practice/JobPostingForm'; // ADDED
import PracticeJobDetailPage from './pages/practice/PracticeJobDetailPage'; // Add this import
import JobBrowsingPage from './pages/JobBrowsingPage'; // Add this import
import RSSJobManagerPage from './pages/admin/RSSJobManagerPage'; // Add this import

// Blog components
import BlogDashboard from './pages/admin/blog/BlogDashboard';
import BlogPostEditor from './pages/admin/blog/BlogPostEditor';
import BlogPostPreview from './pages/admin/blog/BlogPostPreview';
import CategoryManager from './components/blog/CategoryManager';
// Public blog components
import BlogHome from './pages/public/BlogHome';
import BlogPost from './pages/public/BlogPost';
import BlogCategory from './pages/public/BlogCategory';
import PricingPage from './pages/PricingPage';
import FeaturesPage from './pages/FeaturesPage';
import SitemapXML from './components/SitemapXML';

// Define tempStorageUtil - utility for temporary storage management
const tempStorageUtil = {
  setItem: (key, value) => localStorage.setItem(key, JSON.stringify(value)),
  getItem: (key) => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  },
  removeItem: (key) => localStorage.removeItem(key),
  cleanup: () => {
    // Clean up temporary storage items
    const keysToClean = ['temp_invoice_data', 'temp_practice_selection'];
    keysToClean.forEach(key => localStorage.removeItem(key));
    return 0;
  }
};

// Define admin emails - Move to a config file in production
// Parse ADMIN_EMAILS from environment variable
const adminEmailsString = process.env.REACT_APP_ADMIN_EMAILS || '';
export const ADMIN_EMAILS = adminEmailsString.split(',').map(email => email.trim()).filter(email => email);

// Add routes for privacy and terms
export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  INVOICE: '/invoice',
  BILLING: '/billing',
  PROFILE: '/profile',
  REPORTS: '/reports',
  TAX: '/tax',
  INVOICE_HISTORY: '/invoice-history',
  EXPENSES: '/expenses',
  ADMIN: '/admin',
  ADMIN_PRACTICE_MGMT: '/admin/practice-mgmt',
  ADMIN_ARCHIVE_MANAGER: '/admin/daily-log-archive',
  ADMIN_NOTIFICATIONS: '/admin/notifications',
  INVOICE_DETAILS: '/invoice-details/:id',
  SUPPORT: '/support',
  DATA_MERGE: '/admin/data-merge',
  RESOURCES: '/resources',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  PRICING: '/pricing',
  FEATURES: '/features',
  ADMIN_EMAIL: '/admin/email',
  ADMIN_EMAIL_TEMPLATES: '/admin/email-templates',
  ADMIN_REMINDER_DASHBOARD: '/admin/reminder-dashboard', // Add this line
  ADMIN_DASHBOARD: '/admin/dashboard', // Add AdminDashboard route
  ADMIN_USER_MAP: '/admin/user-map', // ADDED: Route for User Map
  ADMIN_PRACTICE_VERIFICATION: '/admin/practice-verification', // ADDED
  ADMIN_RSS_JOBS: '/admin/rss-jobs', // Add this route
  ADMIN_SOCIAL_MEDIA: '/admin/social-media', // Social Media Management
  // ADDED: Blog routes
  BLOG_HOME: '/blog',
  BLOG_POST: '/blog/:slug',
  BLOG_CATEGORY: '/blog/category/:category',
  ADMIN_BLOG: '/admin/blog',
  ADMIN_BLOG_NEW: '/admin/blog/posts/new',
  ADMIN_BLOG_EDIT: '/admin/blog/posts/edit/:id',
  ADMIN_BLOG_PREVIEW: '/admin/blog/posts/preview/:id',
  ADMIN_BLOG_CATEGORIES: '/admin/blog/categories',
  // ADDED: Practice Manager routes
  PRACTICE_DASHBOARD: '/practices/dashboard',
  PRACTICE_JOBS: '/practices/jobs',
  PRACTICE_JOB_NEW: '/practices/jobs/new',
  PRACTICE_VERIFY: '/practices/verify', // ADDED: Route for practice verification
  PRACTICE_PROFILE: '/practices/profile',
  JOBS: '/jobs', // Add this new route
};


// --- Service Worker Update Prompt State ---
// Duplicate import removed. Already imported at the top of the file.

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
          setUpdateAvailable(true);
        }
      });
    }
  }, []);

  const handleUpdate = () => {
    window.location.reload(true); // Force reload to get the new version
  };

  return (
    <HelmetProvider> {/* Wrap entire app with HelmetProvider */}
      <ThemeProvider defaultTheme="light" storageKey="coooly-theme">
        <Router>
          <AppContent />
        </Router>
        <Toaster />
        {updateAvailable && (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, background: '#333', color: '#fff',
            padding: '1em', textAlign: 'center', zIndex: 9999
          }}>
            A new version is available.&nbsp;
            <button onClick={handleUpdate} style={{ color: '#fff', background: '#a259ff', border: 'none', padding: '0.5em 1em', borderRadius: 4 }}>
              Update Now
            </button>
          </div>
        )}
      </ThemeProvider>
    </HelmetProvider>
  );
}

function AppContent() {
  // Core state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true); // NEW: Separate auth loading state
  // Initialize subscriptionStatus from Firestore if possible, then update from cloud function
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); 
  const [isAdmin, setIsAdmin] = useState(false);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [showingPrivacyDialog, setShowingPrivacyDialog] = useState(false);
  const [blockAllNavigation, setBlockAllNavigation] = useState(false);
  const [lastSubscriptionCheck, setLastSubscriptionCheck] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // ADD: New state to track if we're waiting for subscription sync
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // CRITICAL FIX: Add missing ref declarations
  const isRefreshingRef = useRef(false);
  const lastSubscriptionCheckTime = useRef(0);
  // ADD: Track if we've already triggered a refresh this session
  const didInitialSubRefresh = useRef(false);

  // Utils and hooks
  const { toast } = useToast(); // Make sure this is added here
  const auth = getAuth();
  const navigate = useNavigate(); // Add this to fix undefined navigate
  const location = useLocation(); // Get current location

  // Utility function for checking admin status
  const checkIsAdmin = (userEmail) => {
    if (!userEmail) return false;
    // ADMIN_EMAILS is now sourced from the environment variable at the top of the file
    return ADMIN_EMAILS.includes(userEmail);
  };

  // Function for initializing practice data
  const initializePracticeData = async (userId) => {
    if (!userId) return;
    try {
      await practiceDataUtil.initializeForUser(userId);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error initializing practice data:', error);
      }
      toast({
        title: 'Error',
        description: 'Unable to initialize practice data. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // ALL useEffects are at the top level to avoid conditional hooks issues
  
  // Authentication state listener
  useEffect(() => {
    //console.log("App.js: Setting up authentication listener");
    const auth = getAuth();
    
    let unsubscribeUserDoc = () => {}; // Placeholder for user document snapshot unsubscribe

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      //console.log(`App.js: Auth state changed. User: ${firebaseUser ? firebaseUser.uid : 'null'}`);
      
      // CRITICAL FIX: Set authLoading false immediately when auth state resolves
      setAuthLoading(false);
      
      // Unsubscribe from previous user's document listener if it exists
      unsubscribeUserDoc();

      if (firebaseUser) {
        setUser(firebaseUser); // Set basic user object from Firebase Auth
        
        const userDocRef = doc(db, 'users', firebaseUser.uid);

        // ADD: Track last subscription check to prevent loops
        let lastSubscriptionCheckTime = 0;
        const SUBSCRIPTION_CHECK_COOLDOWN = 10000; // 10 seconds

        // Listen for real-time updates to the user document
        unsubscribeUserDoc = onSnapshot(userDocRef, async (userSnapshot) => {
          //console.log("App.js: User document snapshot received.");
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
           // console.log("App.js: User document found in Firestore:", userData);
            
            // Skip repair when onboardingComplete is already any truthy value
            // (boolean true, string 'true', etc.) — repairOnboardingFlags is
            // idempotent but this avoids the async call + object clone on every
            // snapshot for the common case of already-onboarded users.
            const repairedUserData = userData.onboardingComplete
              ? userData
              : await userService.repairOnboardingFlags(firebaseUser.uid, userData);
            const decryptedUserData = decryptUserData(repairedUserData); // Decrypt user data here
            
            const fullCurrentUser = {
              ...firebaseUser,
              ...decryptedUserData, // Use decrypted data
              uid: firebaseUser.uid,
              role: decryptedUserData.role || null // ADDED: Ensure role is always available
            };
            setCurrentUser(fullCurrentUser);

            // Load saved practices from Firestore so InvoiceApp has them on startup
            if (Array.isArray(userData.selectedPractices) && userData.selectedPractices.length > 0) {
              setSelectedPractices(userData.selectedPractices);
            }
            
            // Set initial subscriptionStatus from Firestore data
            // The structure from Firestore is typically { subscription: { level: 'pro', status: 'active', ... } }
            // The structure expected by ProtectedRoute and others from syncStripeCustomer is
            // { synced: true, customerId: '...', hasActiveSubscription: true, subscription: { level: 'pro', ... } }
            // We need to ensure compatibility or adapt. For now, let's assume App.js's subscriptionStatus
            // should hold the object that has the .subscription property.
            if (fullCurrentUser.subscription) {
              ///console.log("App.js: Setting initial subscriptionStatus from Firestore data:", fullCurrentUser.subscription);
              // Create a structure similar to what syncStripeCustomer would return for consistency
              setSubscriptionStatus(prev => ({
                synced: prev?.synced ?? false,
                fromCache: true, // Indicate it's from Firestore cache
                subscription: fullCurrentUser.subscription 
              }));
            } else {
              // If no subscription data in Firestore, initialize as inactive
              setSubscriptionStatus(prev => ({ 
                synced: prev?.synced ?? false, 
                fromCache: true,
                subscription: { status: 'inactive', level: 'free' } 
              }));
            }
            
            setIsAdmin(checkIsAdmin(firebaseUser.email));
            setLoading(false); // App is ready once we have user data or know it doesn't exist
            
            if (decryptedUserData.consentGiven !== 'true' || decryptedUserData.needsPrivacyConsent) {
              //console.log("App.js: User needs to give privacy consent. Blocking navigation.");
              setShowingPrivacyDialog(true);
              setBlockAllNavigation(true);
            } else {
              //console.log("App.js: User has given consent, proceeding with normal flow");
              setShowingPrivacyDialog(false);
              setBlockAllNavigation(false);
              
              if (!decryptedUserData.profileComplete) {
               // console.log("App.js: User profile is not complete, OnboardingRedirect will handle.");
              }
            }

            // ADD: Prevent subscription check loop
            const now = Date.now();
            const timeSinceLastCheck = now - lastSubscriptionCheckTime;
            
            // Only check subscription if enough time has passed AND we don't have recent data
            const needsSubscriptionCheck = timeSinceLastCheck > SUBSCRIPTION_CHECK_COOLDOWN &&
                                          (!userData.subscription || 
                                           !userData.lastSubscriptionSync ||
                                           (now - (userData.lastSubscriptionSync?.toMillis?.() || 0)) > 300000); // 5 minutes
            
            if (needsSubscriptionCheck) {
              lastSubscriptionCheckTime = now;
              await checkUserSubscription(firebaseUser.uid);
            }

          } else {
            try {
              const profileResult = await resolveUserProfileDocument({
                snapshot: userSnapshot,
                getServerSnapshot: getDocFromServer,
                user: firebaseUser,
                userDocRef,
                setDoc,
                serverTimestamp,
                isStale: () => auth.currentUser?.uid !== firebaseUser.uid
              });

              if (profileResult.status === 'existing' || profileResult.status === 'stale') return;
              // The onSnapshot listener will pick this up, and decryptUserData will be called,
              // though for a new user, decryption won't change non-encrypted fields.
            } catch (createError) {
                if (process.env.NODE_ENV === 'development') {
                  console.error("App.js: Error creating user document:", createError);
                }
                setLoading(false);
                 setCurrentUser({
                    ...firebaseUser,
                    error: "Failed to create user data."
                  });
                toast({
                    title: "Account Setup Error",
                    description: "Could not initialize your account data. Please try logging out and in.",
                    variant: "destructive"
                  });
            }
          }
        }, (error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error("App.js: Error in user document snapshot listener:", error);
          }
          setLoading(false);
          setCurrentUser({
            ...firebaseUser,
            error: "Failed to load complete user data."
          });
          toast({
            title: "Error",
            description: "Failed to load your profile data in real-time. Some features may be limited.",
            variant: "destructive"
          });
        });

      } else {
        // User is signed out
        //console.log("App.js: No authenticated user.");
        setUser(null);
        setCurrentUser(null);
        setIsAdmin(false);
        setLoading(false);
        setShowingPrivacyDialog(false);
        setBlockAllNavigation(false);
        setSubscriptionStatus(null);
        tempStorageUtil.cleanup();
      }
    });

    // Clean up listeners on unmount
    return () => {
      unsubscribeAuth();
      unsubscribeUserDoc(); // Unsubscribe from user document listener
    };
  }, [toast]); // Only depend on toast to prevent re-creating the listener
  
  // Enhanced checkUserSubscription with smarter Firestore updates
  const checkUserSubscription = async (userId) => {
    if (!userId || subscriptionLoading) {
      return;
    }

    // CRITICAL FIX: Prevent multiple simultaneous calls using a ref
    if (isRefreshingRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('App.js: Skipping subscription check - already in progress');
      }
      return;
    }

    try {
      isRefreshingRef.current = true; // Set the flag to prevent concurrent calls
      setSubscriptionLoading(true);
      
      const newSubStatus = await subscriptionService.getUserSubscription(userId, { forceRefresh: true }); 

      setSubscriptionStatus(prevStatus => {
        // If result is inactive/free, always overwrite previous state
        if (newSubStatus?.status === 'inactive' || newSubStatus?.level === 'free' || newSubStatus?.source === 'stripe_not_found') {
          return {
            synced: true,
            fromCache: false,
            subscription: {
              status: 'inactive',
              level: 'free',
              availedTrials: {},
              stripeCustomerId: null
            }
          };
        }
      
        // Handle errors: preserve previous active if appropriate
        if (newSubStatus?.error) {
          console.error("App.js: Service error, retaining previous status if active.", newSubStatus.error);
          if (prevStatus?.subscription?.status === 'active') {
            return {
              ...prevStatus,
              lastCheckError: newSubStatus.error,
              errorSource: newSubStatus.source || 'service_error',
              synced: false
            };
          }
        }
      
        // Wrap flat result into { subscription: … } shape
        if (newSubStatus) {
          // Already wrapped by syncStripeCustomer?
          if (newSubStatus.subscription) {
            return { ...newSubStatus, synced: true, fromCache: false };
          }
          return {
            synced: true,
            fromCache: false,
            subscription: newSubStatus
          };
        }
      
        // Fallback when no status returned
        return {
          synced: false,
          fromCache: false,
          subscription: { status: 'inactive', level: 'free' },
          error: 'null_new_status_from_service',
          source: 'checkUserSubscription_fallback'
        };
      });

      // ENHANCED: More intelligent Firestore update decision
      const shouldUpdateFirestore = (() => {
        if (!currentUser?.subscription) return true;
        
        const current = currentUser.subscription;
        const incoming = newSubStatus;
        
        // Don't update if data is essentially the same
        const hasSignificantChanges = 
          current.status !== incoming.status ||
          current.level !== incoming.level ||
          current.cancelAtPeriodEnd !== incoming.cancelAtPeriodEnd ||
          current.stripeSubscriptionId !== incoming.stripeSubscriptionId ||
          Math.abs((current.currentPeriodEnd || 0) - (incoming.currentPeriodEnd || 0)) > 3600; // 1 hour difference
        
        // ADD: Don't update if we just updated recently
        const lastSync = currentUser.lastSubscriptionSync?.toMillis?.() || 0;
        const timeSinceLastSync = Date.now() - lastSync;
        const recentlyUpdated = timeSinceLastSync < 60000; // 1 minute
        
        return hasSignificantChanges && !recentlyUpdated && newSubStatus.source === 'cloud_function_success';
      })();

      if (shouldUpdateFirestore) {
        try {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            subscription: newSubStatus,
            lastSubscriptionSync: new Date(), // Use Date() instead of serverTimestamp()
            updatedAt: new Date() // Use Date() instead of serverTimestamp()
          });
        } catch (firestoreError) {
          // Don't throw - we still have the subscription data
          console.warn('Failed to update subscription in Firestore:', firestoreError);
        }
      }

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("App.js: Exception while checking subscription via service:", error);
      }
      setSubscriptionStatus(prevStatus => {
        if (prevStatus && prevStatus.subscription && prevStatus.subscription.status === 'active') {
          if (process.env.NODE_ENV === 'development') {
            console.warn("App.js: Subscription check (service) threw exception, but retaining previous active status. Previous status:", prevStatus);
          }
          return { ...prevStatus, lastCheckError: error.message || 'exception_during_service_check', errorSource: 'exception_handler_service_catch', synced: false };
        }
        return { subscription: { status: 'inactive', level: 'free' }, error: error.message, source: 'exception_handler_service_catch_default', synced: false };
      });
    } finally {
      setSubscriptionLoading(false);
      isRefreshingRef.current = false; // Always clear the flag
    }
  };

  // Enhanced subscription sync function with loop prevention
  const syncSubscription = useCallback(async (userId, forceRefresh = false) => {
    if (!userId || subscriptionLoading) {
     // console.log('App.js: Sync skipped - no userId or already loading');
      return;
    }

    //console.log('App.js: Checking subscription for user:', userId, 'via subscriptionService.getUserSubscription');
    setSubscriptionLoading(true);

    try {
      const options = forceRefresh ? { forceRefresh: true } : {};
      const subscriptionData = await subscriptionService.getUserSubscription(userId, options);
     // console.log('App.js: Subscription check result from subscriptionService:', subscriptionData);

      // CRITICAL FIX: Check if we need to update Firestore before writing
      const shouldUpdateFirestore = (() => {
        if (!currentUser?.subscription) return true; // No existing data
        
        const current = currentUser.subscription;
        const incoming = subscriptionData;
        
        // Compare key fields that matter
        const keyFieldsChanged = 
          current.status !== incoming.status ||
          current.level !== incoming.level ||
          current.cancelAtPeriodEnd !== incoming.cancelAtPeriodEnd ||
          current.stripeSubscriptionId !== incoming.stripeSubscriptionId ||
          current.currentPeriodEnd !== incoming.currentPeriodEnd;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('App.js: Firestore update check:', {
            keyFieldsChanged,
            currentStatus: current.status,
            incomingStatus: incoming.status,
            currentCancelAtPeriodEnd: current.cancelAtPeriodEnd,
            incomingCancelAtPeriodEnd: incoming.cancelAtPeriodEnd
          });
        }
        
        return keyFieldsChanged;
      })();

      if (shouldUpdateFirestore && subscriptionData.source === 'cloud_function_success') {
       // console.log('App.js: Updating Firestore with fresh subscription data');
        try {
          const userRef = doc(db, 'users', userId);
          
          // CRITICAL FIX: Filter out undefined values before updating Firestore
          const cleanedSubscriptionData = Object.fromEntries(
            Object.entries(subscriptionData).filter(([_, value]) => value !== undefined)
          );
          
          await updateDoc(userRef, {
            subscription: cleanedSubscriptionData,
            lastSubscriptionSync: new Date(),
            updatedAt: serverTimestamp()
          });
          //console.log('App.js: Successfully updated subscription in Firestore');
        } catch (firestoreError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('App.js: Failed to update subscription in Firestore:', firestoreError);
          }
          // Don't throw - we still have the subscription data
        }
      } else {
        //console.log('App.js: Skipping Firestore update - no significant changes or not from cloud function');
      }

      // Always update the local state regardless of Firestore write
      setSubscriptionStatus({
        subscription: subscriptionData,
        synced: subscriptionData.synced !== false,
        fromCache: subscriptionData.fromCache || false,
        source: subscriptionData.source || 'unknown',
        lastCheckError: subscriptionData.error || null
      });

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('App.js: Error in subscription sync:', error);
      }
      setSubscriptionStatus(prev => ({
        ...prev,
        lastCheckError: error.message,
        synced: false
      }));
    } finally {
      setSubscriptionLoading(false);
    }
  }, [subscriptionLoading, currentUser]);

  // Add effect to clean up temporary storage periodically
  useEffect(() => {
    // Clean up on initial load
    const removed = tempStorageUtil.cleanup();
    if (removed > 0) {
     // console.log(`Cleaned up ${removed} expired temporary storage items`);
    }
    
    // Set up periodic cleanup
    const cleanupInterval = setInterval(() => {
      const removed = tempStorageUtil.cleanup();
      if (removed > 0) {
        //console.log(`Cleaned up ${removed} expired temporary storage items`);
      }
    }, 10 * 60 * 1000); // Run every 10 minutes
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Login success handler
  const handleLoginSuccess = async (loggedInUser) => {
    if (!loggedInUser) return;
    
    try {
      //console.log('Login success for user:', loggedInUser.uid);
      const userDocRef = doc(db, 'users', loggedInUser.uid);
      await updateDoc(userDocRef, {
        lastLogin: new Date().toISOString()
      });

      // Initialize practice data only after successful login
      initializePracticeData(loggedInUser);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating last login:', error);
      }
    }
  };

  // Privacy policy handlers
  const handlePrivacyPolicyAccepted = async () => {
    try {
      if (!user || !user.uid) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Cannot update privacy consent: No authenticated user");
        }
        return;
      }
      
     // console.log("App.js: Updating privacy consent for user:", user.uid);
      
      // Update Firestore with consent given
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        consentGiven: 'true',
        needsPrivacyConsent: false,
        lastUpdated: new Date().toISOString()
      });
      
      // Local state update
      setShowingPrivacyDialog(false);
      setBlockAllNavigation(false);
      
      //console.log("App.js: Privacy consent updated successfully");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("App.js: Error updating privacy consent:", error);
      }
      toast({
        title: "Error",
        description: "Failed to save your privacy consent. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handlePrivacyDialogOpen = (isOpen) => {
    setShowingPrivacyDialog(isOpen);
    setBlockAllNavigation(isOpen);
  };

  // Subscription update handler
  const handleSubscriptionUpdate = (updatedSubscription) => {
    //console.log('Subscription updated:', updatedSubscription);
    
    if (!updatedSubscription) return;
    
    // IMPORTANT FIX: Ensure consistent subscription format with stripeCustomerId included
    if (updatedSubscription.status && !updatedSubscription.subscription) {
      // If the user has a stripeCustomerId in their user document, include it in the subscription data
      const customerIdToUse = updatedSubscription.stripeCustomerId || 
                             currentUser?.stripeCustomerId;
      
      setSubscriptionStatus({
        synced: true,
        fromCache: false,
        subscription: {
          ...updatedSubscription,
          stripeCustomerId: customerIdToUse
        }
      });
    } else if (updatedSubscription.subscription) {
      // If it's already in the correct format, ensure the stripeCustomerId is included
      const customerIdToUse = updatedSubscription.subscription.stripeCustomerId || 
                             updatedSubscription.stripeCustomerId ||
                             currentUser?.stripeCustomerId;
      
      setSubscriptionStatus({
        ...updatedSubscription,
        subscription: {
          ...updatedSubscription.subscription,
          stripeCustomerId: customerIdToUse || updatedSubscription.subscription.stripeCustomerId
        }
      });
    } else {
      // Already in correct format with .subscription property
      setSubscriptionStatus(updatedSubscription);
    }
  };

  // Practice selection handler
  const handlePracticeSelection = async (updatedPractices) => {
    if (!user) return;
    
    try {
      //console.log("App.js: handlePracticeSelection called - NOT triggering subscription check");
      setSelectedPractices(updatedPractices);
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        selectedPractices: updatedPractices,
        lastUpdated: new Date()
      });
      
      toast({
        title: "Practices Updated",
        description: "Your practice selection has been saved",
        variant: "success"
      });

      // Navigate to the new invoice app component after selecting practices
      navigate(ROUTES.INVOICE);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving practice selection:', error);
      }
      toast({
        title: "Error",
        description: "Failed to save practice selection",
        variant: "destructive"
      });
    }
  };

  // Sign out handler
  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error signing out:', error);
      }
      toast({
        title: "Error",
        description: "Failed to sign out",
        variant: "destructive"
      });
    }
  };

  // Add this utility function inside your App component or as a separate function
  const decryptUserData = useCallback((encryptedData) => {
    if (!encryptedData) return encryptedData;
    
    // Create a new object to avoid mutating the original
    const decrypted = { ...encryptedData };
    
    // Decrypt fields that are strings and not in the exclusion list
    const fieldsToExcludeFromDecryption = [
      'uid', 
      'email', 
      'consentGiven',
      'provider',
      'stripeCustomerId',
      'selectedPlan',
      'lastStep',
      'createdAt', 
      'updatedAt', 
      'lastLogin', 
      'lastUpdated',
      'trialStartDate',
      'trialEnd', 
      'currentPeriodEnd',
      'role', // ADDED: Ensure role is not encrypted
    ];

    Object.keys(decrypted).forEach(key => {
      // Only try to decrypt if it's a string and not in the exclusion list
      if (typeof decrypted[key] === 'string' && !fieldsToExcludeFromDecryption.includes(key)) {
        try {
          decrypted[key] = decryptData(decrypted[key]);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error(`Error decrypting ${key}:`, error);
          }
          // Keep the original value if decryption fails
        }
      }
    });
    
    return decrypted;
  }, []); // Empty dependency array if decryptData itself doesn't depend on AppContent's scope

  // Effect for redirecting users with 'free' or ended subscriptions
  useEffect(() => {
    // CRITICAL FIX: Only run billing redirects after subscription is fully synced
    if (user && currentUser && subscriptionStatus?.subscription && subscriptionStatus.synced === true) {
      // Skip any redirects if user needs to give privacy consent first
      if (currentUser.consentGiven === 'false' || currentUser.needsPrivacyConsent === true) {
       // console.log("App.js: Skipping subscription redirect - user needs to give privacy consent first");
        return; // Don't perform any redirects until privacy consent is given
      }
      
      // ADDED: Skip subscription redirects for Practice Managers entirely
      if (currentUser.role === 'Practice Manager') {
       // console.log("App.js: Skipping subscription redirect - Practice Manager doesn't need billing");
        return;
      }
      
      // Only proceed with redirects if privacy consent has been given
      const isAdmin = currentUser.email && checkIsAdmin(currentUser.email);
      const level = subscriptionStatus.subscription.level || 'free';
      const status = subscriptionStatus.subscription.status || 'inactive';
      
     // console.log(`App.js: Billing redirect check - level: '${level}', status: '${status}', synced: ${subscriptionStatus.synced}`);
      
      // No redirects if we're on one of these paths
      const noRedirectPaths = [
        ROUTES.BILLING, 
        ROUTES.PRIVACY, 
        ROUTES.TERMS, 
        ROUTES.PROFILE,
        '/admin', // Starts with check for admin routes
        '/practices', // ADDED: Don't redirect Practice Managers from their pages
      ];
      
      // Admins can access all routes
      if (isAdmin) return;
      
      // Don't redirect if already on an excluded path
      if (noRedirectPaths.some(path => 
        path.endsWith('/') 
          ? location.pathname.startsWith(path) 
          : location.pathname === path || location.pathname.startsWith(path + '/')
      )) {
        return;
      }
      
      // UPDATED: Include 'trialing' and 'past_due' as valid statuses that should NOT redirect to billing
      const isEffectivelyFree = level === 'free' || 
                                (status !== 'active' && 
                                 status !== 'trialing' && // ADD: trialing is valid
                                 status !== 'past_due' && // CRITICAL FIX: past_due should not be treated as free
                                 (status === 'ended' || status === 'canceled' || status === 'incomplete_expired'));

      // Define routes that are always accessible, even with a 'free' status
      const accessibleRoutes = [
        ROUTES.BILLING,
        ROUTES.PROFILE,
        ROUTES.SUPPORT,
        ROUTES.PRIVACY,
        ROUTES.TERMS,
        ROUTES.DASHBOARD, // Dashboard might show limited info or prompts
        ROUTES.RESOURCES,
        // ADDED: Practice Manager routes are always accessible
        ROUTES.PRACTICE_DASHBOARD,
        ROUTES.PRACTICE_JOBS,
        ROUTES.PRACTICE_JOB_NEW,
      ];

      // Check if current path is one of the base admin paths
      const isAdminPath = Object.values(ROUTES).some(route => 
        route.startsWith('/admin') && location.pathname.startsWith(route)
      );

      // ADDED: Check if current path is a practice management path
      const isPracticePath = Object.values(ROUTES).some(route => 
        route.startsWith('/practices') && location.pathname.startsWith(route)
      );

      if (isEffectivelyFree && !isAdmin && !accessibleRoutes.includes(location.pathname) && !isAdminPath && !isPracticePath) {
        // Allow access to admin routes if user is admin
        if (location.pathname !== ROUTES.BILLING) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`App.js: User with level '${level}' and status '${status}' redirected to billing from ${location.pathname}.`);
            }
            toast({
                title: "Subscription Required",
                description: "Please update your subscription to access this feature.",
                variant: "warning",
            });
            navigate(ROUTES.BILLING, { replace: true });
        }
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log("App.js: Billing redirect skipped - waiting for subscription sync completion", {
          hasUser: !!user,
          hasCurrentUser: !!currentUser,
          hasSubscriptionStatus: !!subscriptionStatus,
          subscriptionSynced: subscriptionStatus?.synced
        });
      }
    }
  }, [subscriptionStatus, currentUser, user, location, navigate, toast, isAdmin]); // Added isAdmin

  // Main app rendering with clean routing structure
  return (
    <div className="min-h-screen bg-[#1E1E2D] text-white">
      {/* Show loading or navigation based on privacy dialog state */}
      {showingPrivacyDialog || blockAllNavigation ? (
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <Splashscreen 
            onLoginSuccess={handleLoginSuccess} 
            onPrivacyPolicyAccepted={handlePrivacyPolicyAccepted}
            onPrivacyDialogOpen={handlePrivacyDialogOpen}
          />
        </div>
      ) : (
        <Routes>
          {/* Publicly accessible routes */}
          <Route path={ROUTES.PRIVACY} element={<PrivacyPolicyPage />} />
          <Route path={ROUTES.TERMS} element={<TermsOfServicePage />} />
          <Route path={ROUTES.PRICING} element={<PricingPage />} />
          <Route path={ROUTES.FEATURES} element={<FeaturesPage />} />
          
          {/* Public Blog routes */}
          <Route path={ROUTES.BLOG_HOME} element={<BlogHome />} />
          <Route path={ROUTES.BLOG_POST} element={<BlogPost />} />
          <Route path={ROUTES.BLOG_CATEGORY} element={<BlogCategory />} />

          {/* Home route with smart redirection */}
          <Route path={ROUTES.HOME} element={
            // CRITICAL FIX: Show minimal loading during auth resolution
            authLoading ? (
              <div className="min-h-screen flex items-center justify-center bg-[#1E1E2D]">
                <div className="text-center">
                  <LoadingSpinner size="large" />
                  <p className="mt-4 text-gray-400">Loading...</p>
                </div>
              </div>
            ) : user ? (
              // MODIFIED: Enhanced check to include role completion
              !currentUser?.profileComplete || !currentUser?.role ? (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]">
                  <div className="bg-[#2A2A3F] border border-purple-500/30 max-w-2xl rounded-lg overflow-hidden max-h-[90vh] w-[90vw]">
                    <ProfileSettings
                      profileData={currentUser}
                      onSave={async (updatedProfile) => {
                        try {
                          await userService.rebuildUserProfile(user, updatedProfile);
                          toast({
                            title: "Profile Updated",
                            description: "Your profile has been saved successfully",
                            variant: "success"
                          });
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to update profile",
                            variant: "destructive"
                          });
                        }
                      }}
                      onClose={() => {
                        // MODIFIED: Enhanced message for role requirement
                        const missingRole = !currentUser?.role;
                        const missingProfile = !currentUser?.profileComplete;
                        
                        let message = "Please complete your profile to continue.";
                        if (missingRole && missingProfile) {
                          message = "Please complete your profile and select your professional role to continue.";
                        } else if (missingRole) {
                          message = "Please select your professional role to continue.";
                        }
                        
                        toast({
                          title: "Profile Required",
                          description: message,
                          variant: "warning"
                        });
                      }}
                    />
                  </div>
                </div>
              ) : (
                <OnboardingRedirect 
                  user={user} 
                  currentUser={currentUser}
                  subscriptionStatus={subscriptionStatus}
                  appLoading={loading}
                  isAdmin={isAdmin}
                >
                  <Navigate to={ROUTES.DASHBOARD} replace />
                </OnboardingRedirect>
              )
            ) : (
              <Splashscreen 
                onLoginSuccess={handleLoginSuccess} 
                onPrivacyPolicyAccepted={handlePrivacyPolicyAccepted}
                onPrivacyDialogOpen={handlePrivacyDialogOpen}
              />
            )
          } />

          {/* Authenticated routes */}
          <Route element={<MainLayout user={user} currentUser={currentUser} isAdmin={isAdmin} handleSignOut={handleSignOut} subscriptionStatus={subscriptionStatus} />}>
            {/* Dashboard Route */}
            <Route path={ROUTES.DASHBOARD} element={
              <ProtectedRoute 
                subscriptionStatus={subscriptionStatus}
                currentUser={currentUser}
              >
                <DashboardPage user={user} currentUser={currentUser} subscriptionStatus={subscriptionStatus} />
              </ProtectedRoute>
            } />

            {/* Invoice page */}
            <Route path={ROUTES.INVOICE} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <InvoiceAppWithContext 
                  user={user} 
                  initialSelectedPractices={selectedPractices}
                  subscriptionStatus={subscriptionStatus}
                />
              </ProtectedRoute>
            } />

            {/* Profile settings */}
            <Route path={ROUTES.PROFILE} element={
              <ProtectedRoute 
                subscriptionStatus={subscriptionStatus}
                currentUser={currentUser}
              >
                <ProfileSettings
                  profileData={currentUser}
                  onSave={async (updatedProfileFromSettings) => {
                    try {
                      // ProfileSettings.jsx has already saved the data to Firestore.
                      
                      // Refresh subscription status immediately after profile save
                      if (user?.uid) {
                        await checkUserSubscription(user.uid); // This updates the subscriptionStatus state
                      }
                      
                      toast({
                        title: "Profile Updated",
                        description: "Your profile has been saved successfully",
                        variant: "success"
                      });
                      
                      // Navigation logic:
                      // Use updatedProfileFromSettings for profile completeness.
                      // Use the component's subscriptionStatus state, which checkUserSubscription just (potentially conditionally) updated.
                      const profileIsNowComplete = updatedProfileFromSettings.profileComplete === true;
                      const roleIsSelected = updatedProfileFromSettings.role && updatedProfileFromSettings.role.trim() !== ''; // ADDED: Check role completion
                      // Use consent from updatedProfileFromSettings if available, otherwise from currentUser
                      const consentIsGiven = updatedProfileFromSettings.consentGiven === 'true' || (currentUser && currentUser.consentGiven === 'true');
                      
                      // Use the subscriptionStatus from App.js's state
                      const currentSubStatus = subscriptionStatus; // Get the latest state
                      const billingIsComplete = (currentUser && currentUser.billingComplete === true) || (currentSubStatus && currentSubStatus.status === 'active');

                      if (process.env.NODE_ENV === 'development') {
                        console.log("App.js Profile Save - Navigation Check:", {
                          profileIsNowComplete,
                          roleIsSelected, // ADDED: Log role selection status
                          consentIsGiven,
                          billingIsComplete,
                          currentSubStatus
                        });
                      }

                      // MODIFIED: Include role check in navigation logic with Practice Manager support
                      if (consentIsGiven && profileIsNowComplete && roleIsSelected) {
                        // ADDED: Role-aware navigation after profile completion
                        if (updatedProfileFromSettings.role === 'Practice Manager') {
                          // Practice Managers skip billing and go to practice dashboard
                          //console.log("App.js: Practice Manager profile complete, navigating to Practice Dashboard");
                          navigate(ROUTES.PRACTICE_DASHBOARD);
                        } else {
                          // For other roles, check billing status
                          if (billingIsComplete) {
                            // Regular users with billing complete go to dashboard
                           // console.log("App.js: Regular user with billing complete, navigating to Dashboard");
                            navigate(ROUTES.DASHBOARD);
                          } else {
                            // Regular users without billing go to billing
                           // console.log("App.js: Regular user needs billing, navigating to Billing");
                            navigate(ROUTES.BILLING, { state: { onboarding: true, forceShowPlans: true } });
                          }
                        }
                      } else {
                        // Stay on profile if role is not selected, even if profile is otherwise complete
                        if (!roleIsSelected) {
                          toast({
                            title: "Role Required",
                            description: "Please select your professional role to continue.",
                            variant: "warning"
                          });
                          return; // Don't navigate away
                        }
                        
                        // ADDED: Fallback navigation for Practice Managers
                        if (updatedProfileFromSettings.role === 'Practice Manager') {
                         // console.log("App.js: Practice Manager fallback navigation to Practice Dashboard");
                          navigate(ROUTES.PRACTICE_DASHBOARD);
                        } else {
                          navigate(ROUTES.DASHBOARD); 
                        }
                      }
                    } catch (error) {
                      if (process.env.NODE_ENV === 'development') {
                        console.error("Error in App.js after profile save:", error);
                      }
                      toast({
                        title: "Error",
                        description: "Failed to complete post-profile-save actions. " + error.message,
                        variant: "destructive"
                      });
                    }
                  }}
                  onClose={() => navigate(ROUTES.DASHBOARD)}
                />
              </ProtectedRoute>
            } />

            {/* Billing page */}
            <Route path={ROUTES.BILLING} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <BillingSettings 
                  user={user}
                  onSubscriptionUpdate={handleSubscriptionUpdate}
                  isOnboarding={completingOnboarding || 
                              currentUser?.consentGiven === 'true' && 
                              currentUser?.profileComplete === true && 
                              !currentUser?.onboardingComplete ||
                              currentUser?.billingInProgress}
                  forceShowPlans={!subscriptionStatus || !subscriptionStatus.subscription || subscriptionStatus.subscription.status !== 'active'}
                />
              </ProtectedRoute>
            } />

            {/* Reports page (Pro feature) */}
            <Route path={ROUTES.REPORTS} element={
              <ProtectedRoute requiredSubscription="pro" subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <Reports user={user} />
              </ProtectedRoute>
            } />

            {/* Invoice history (Pro feature) */}
            <Route path={ROUTES.INVOICE_HISTORY} element={
              <ProtectedRoute requiredSubscription="pro" subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <InvoiceHistory user={user} />
              </ProtectedRoute>
            } />

            {/* Expenses page (Pro feature) */}
            <Route path={ROUTES.EXPENSES} element={
              <ProtectedRoute requiredSubscription="pro" subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <ExpensesPage user={user } />
              </ProtectedRoute>
            } />

            {/* Tax calculator (Pro feature) */}
            <Route path={ROUTES.TAX} element={
              <ProtectedRoute requiredSubscription="pro" subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <TaxDashboard user={user } />
              </ProtectedRoute>
            } />

            {/* Admin routes - only accessible by admins */}
            <Route path={ROUTES.ADMIN_PRACTICE_MGMT} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <PracticeMgmtAdmin /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_DASHBOARD} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <AdminDashboard /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />
            
            <Route path={ROUTES.ADMIN_ARCHIVE_MANAGER} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <ArchiveManager /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_NOTIFICATIONS} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <NotificationManager /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.INVOICE_DETAILS} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <InvoiceDetails />
              </ProtectedRoute>
            } />

            <Route path={ROUTES.SUPPORT} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <SupportPage />
              </ProtectedRoute>
            } />

            <Route path={ROUTES.DATA_MERGE} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <DataMergeTool /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.RESOURCES} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <ResourcesPage />
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_EMAIL} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <EmailAdminPage /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path="/admin/email-templates" element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <EmailTemplateAdmin /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_REMINDER_DASHBOARD} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <ReminderDashboard /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_USER_MAP} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <UserMapPage /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            {/* ADDED: Practice Verification Admin Route */}
            <Route path={ROUTES.ADMIN_PRACTICE_VERIFICATION} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <PracticeVerificationAdmin /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            {/* ADDED: RSS Job Manager Route */}
            <Route path={ROUTES.ADMIN_RSS_JOBS} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <RSSJobManagerPage /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            {/* ADDED: Social Media Management Route */}
            <Route path={ROUTES.ADMIN_SOCIAL_MEDIA} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <SocialMediaManager /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            {/* ADDED: Blog Management Routes */}
            <Route path={ROUTES.ADMIN_BLOG} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <BlogDashboard /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_BLOG_NEW} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <BlogPostEditor /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_BLOG_EDIT} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <BlogPostEditor /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_BLOG_PREVIEW} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <BlogPostPreview /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path={ROUTES.ADMIN_BLOG_CATEGORIES} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <CategoryManager /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            <Route path="/admin/blog/sitemap-generator" element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {isAdmin ? <SitemapXML /> : <Navigate to={ROUTES.HOME} replace />}
              </ProtectedRoute>
            } />

            {/* Practice Manager routes - only accessible by Practice Managers */}
            <Route path={ROUTES.PRACTICE_DASHBOARD} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {currentUser?.role === 'Practice Manager' ? 
                  <PracticeDashboardPage user={user} currentUser={currentUser} /> : 
                  <Navigate to={ROUTES.DASHBOARD} replace />
                }
              </ProtectedRoute>
            } />

            <Route path={ROUTES.PRACTICE_JOBS} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {currentUser?.role === 'Practice Manager' ? 
                  <PracticeViewJobsPage user={user} currentUser={currentUser} /> : 
                  <Navigate to={ROUTES.DASHBOARD} replace />
                }
              </ProtectedRoute>
            } />
            
            {/* Add this new route for job details */}
            <Route path={`${ROUTES.PRACTICE_JOBS}/:jobId`} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {currentUser?.role === 'Practice Manager' ? 
                  <PracticeJobDetailPage user={user} currentUser={currentUser} /> : 
                  <Navigate to={ROUTES.DASHBOARD} replace />
                }
              </ProtectedRoute>
            } />

            {/* ADDED: Route for job editing */}
            <Route path={`${ROUTES.PRACTICE_JOBS}/edit/:jobId`} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {currentUser?.role === 'Practice Manager' ? 
                  <PracticeJobPostingPage user={user} currentUser={currentUser} isEditing={true} /> : 
                  <Navigate to={ROUTES.DASHBOARD} replace />
                }
              </ProtectedRoute>
            } />

            <Route path={ROUTES.PRACTICE_JOB_NEW} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {currentUser?.role === 'Practice Manager' ? 
                  <PracticeJobPostingPage user={user} currentUser={currentUser} /> : 
                  <Navigate to={ROUTES.DASHBOARD} replace />
                }
              </ProtectedRoute>
            } />

            {/* ADDED: Practice Verification Route */}
            <Route path={ROUTES.PRACTICE_VERIFY} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                {currentUser?.role === 'Practice Manager' ? 
                  <ManagePracticesPage user={user} currentUser={currentUser} /> : 
                  <Navigate to={ROUTES.DASHBOARD} replace />
                }
              </ProtectedRoute>
            } />

            {/* ADDED: Job browsing route for normal users */}
            <Route path={ROUTES.JOBS} element={
              <ProtectedRoute subscriptionStatus={subscriptionStatus} currentUser={currentUser}>
                <JobBrowsingPage user={user} currentUser={currentUser} />
              </ProtectedRoute>
            } />
          </Route>
        </Routes>
      )}

      <Toaster />
    </div>
  );
}

// NEW: Component to handle Stripe return and redirect appropriately
function BillingReturnHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleReturn = async () => {
      try {
        // Check URL params for Stripe session info
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        const status = urlParams.get('status');
        
        if (sessionId && status === 'success') {
          // Give a moment for any webhook processing
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Redirect to billing with success message
          navigate(ROUTES.BILLING + '?payment=success', { replace: true });
        } else if (status === 'cancel') {
          // Redirect to billing with cancel message
          navigate(ROUTES.BILLING + '?payment=cancelled', { replace: true });
        } else {
          // Unknown state, go to billing
          navigate(ROUTES.BILLING, { replace: true });
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error handling billing return:', error);
        }
        navigate(ROUTES.BILLING, { replace: true });
      } finally {
        setProcessing(false);
      }
    };

    handleReturn();
  }, [navigate, location]);

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1E1E2D]">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-400">Processing your payment...</p>
          <p className="mt-2 text-sm text-gray-500">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return null;
}

export default App;